/**
 * Pure phase-transition logic for the Notice Advisor wizard. The branching that
 * depends on computed IA math / streamlined thresholds lives server-side
 * (`/notices/advisor/recommend/`); this module only owns the deterministic
 * navigation the client drives (init routing, prep guards, tax → options).
 */
import type { AdvisorInput } from "@/features/notices/types";

export type AdvisorPhase =
  | "init"
  | "notice"
  | "abate"
  | "prep"
  | "bus_prep"
  | "tax_due"
  | "bus_tax_due"
  | "options"
  | "options2"
  | "blocked"
  | "result";

export type ClientType = "I" | "B";

/** The answers accumulated across phases (superset of the API input). */
export type AdvisorAnswers = Partial<AdvisorInput>;

/** Why an individual path is blocked before reaching a resolution. */
export type BlockReason = "filefirst" | "bankruptcy" | null;

export interface BlockedResult {
  phase: "blocked";
  reason: Exclude<BlockReason, null>;
}

/**
 * Compute the next phase from `init` given the chosen intent + the active
 * client's type. Returns null for intents handled asynchronously by the caller
 * (they call the recommend endpoint instead of navigating locally).
 */
export function nextFromInit(
  intent: AdvisorInput["intent"],
  clientType: ClientType,
): AdvisorPhase {
  if (intent === "notice") return "notice";
  if (intent === "abate") return "abate";
  return clientType === "I" ? "prep" : "bus_prep";
}

/** Evaluate the individual prep guards. Returns a block reason, or null to
 * continue to the tax-owed phase. */
export function prepGuard(answers: AdvisorAnswers): BlockReason {
  if (answers.filed === false) return "filefirst";
  if (answers.bankrupt === true) return "bankruptcy";
  return null;
}

/** Next phase after prep (individual): blocked, or on to tax-owed. */
export function nextFromPrep(answers: AdvisorAnswers): AdvisorPhase {
  return prepGuard(answers) ? "blocked" : "tax_due";
}

/** Next phase after the business prep questions: always the business
 * tax-owed phase (the TFRP warning surfaces in the recommendation). */
export function nextFromBusPrep(): AdvisorPhase {
  return "bus_tax_due";
}

/** The options phase for a client type (individual vs business). */
export function optionsPhaseFor(clientType: ClientType): AdvisorPhase {
  return clientType === "I" ? "options" : "options2";
}

/** Individual resolution options shown on the `options` phase. */
export const INDIVIDUAL_OPTIONS: { value: string; label: string }[] = [
  { value: "ia", label: "Make monthly payments (Installment Agreement)" },
  {
    value: "ia_lower",
    label: "Installment Agreement with a lower monthly payment (financial disclosure)",
  },
  { value: "oic", label: "Offer less than the total owed (Offer in Compromise)" },
  { value: "cnc", label: "Unable to make any payments (Currently Not Collectible)" },
];

/** Business resolution options shown on the `options2` phase. */
export const BUSINESS_OPTIONS: { value: string; label: string }[] = [
  { value: "ia", label: "Make monthly payments (Installment Agreement)" },
  { value: "oic", label: "Offer less than the total owed (Offer in Compromise)" },
  { value: "cnc", label: "Be considered Currently Not Collectible" },
];

export const BLOCK_MESSAGE: Record<Exclude<BlockReason, null>, string> = {
  filefirst: "All delinquent returns must be filed before pursuing a collection alternative.",
  bankruptcy:
    "A collection alternative can't be submitted while the client is in bankruptcy.",
};
